// MovieNightCard — the UI for Cruz's Movie Night, the weekly two-player
// ritual in the Pack House channel (Haley, 2026-09-01). Rendered by the
// integrator at the top of the channel; owns nothing but the ritual's card.
//
// The card follows one movie_nights row through its phases:
//   picks     → Tuesday 3pm: Cruz has posted three 80s titles. Two-key vote —
//               the first tap is a vote, the second player's tap LOCKS.
//   locked    → the winning title, a countdown, and the host's link box.
//   countdown → the final hour: the countdown pulses, "Enter the living room".
//   live      → a compact strip; the synced player itself is mounted by the
//               integrator via onEnterLivingRoom, never by this card.
//   after     → the ticket stub, kept to the Shelf for BOTH players by the
//               movie_night_stub RPC (keepsakes are owner-only RLS, so the
//               client can't insert the other player's copy itself).
//
// Rules of the vote (why resolveVote lives in movieNight.js and not here):
// nothing locks until both players have acted, and if they disagree the
// second tap wins — she saw the first vote and chose anyway. This card only
// persists whatever resolveVote hands back, so the rule has exactly one home.
//
// Copyright: the card never suggests, curates or hardcodes a video link. The
// catalog is titles + years; the host pastes whatever URL she has the right
// to watch, and parseVideoUrl only classifies it (YouTube / Internet Archive /
// her own player) so the integrator knows which player to mount.
//
// Mobile-first for the iPhone 13 mini (375px): every tap target is ≥44px,
// the link input is 16px so iOS doesn't zoom, and the only motion (the
// final-hour pulse) collapses under prefers-reduced-motion.

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { supabase } from './supabaseClient.js';
import { resolveVote, phaseFor, parseVideoUrl, ticketStub, EIGHTIES_CATALOG } from './movieNight.js';

const TZ = 'America/New_York';
const PHASES = ['upcoming', 'picks', 'locked', 'countdown', 'live', 'after'];
// A night whose status never left 'picks'/'locked'/'live' (nobody pressed
// play, nobody rolled credits) stops being "the next night" a day after its
// showtime. Without this floor the oldest unfinished row wins the load query
// forever and Cruz's next Tuesday post never reaches the card.
const STALE_NIGHT_MS = 24 * 3600 * 1000;

// ─── Small pure helpers (module-level so the home card can share them) ───────

/** members arrives either as an array of profiles or as the channel
 * profiles-cache map ({ userId: profile }); accept both, keep an array. */
function memberList(members) {
  if (!members) return [];
  // The profiles cache is keyed by user id and its rows don't always carry
  // one, so the KEY is the id of last resort — without it the other player
  // vanished from the card (her vote showed as nobody's, no "Lock" tile).
  const entries = Array.isArray(members) ? members.map(m => [null, m]) : Object.entries(members);
  return entries.map(([key, m]) => m && ({
    id: m.user_id || m.id || key || null,
    name: m.display_name || m.name || '',
  })).filter(m => m && m.id);
}

function nameOf(list, uid, fallback = 'Someone') {
  const m = list.find(x => x.id === uid);
  return (m && m.name) || fallback;
}

/** Seat "numbers" on the stub are the players' initials — H · N — because a
 * two-seat theater doesn't need row letters, it needs the two of them. */
function initialsOf(name) {
  return String(name || '').trim().split(/\s+/).map(w => w[0] || '').join('').slice(0, 2).toUpperCase() || '?';
}

/** picks may be stored as {title, year} objects, catalog indexes, or bare
 * titles; normalize to {title, year} via the catalog when needed. */
function pickOf(p) {
  if (p == null) return null;
  if (typeof p === 'number') return EIGHTIES_CATALOG?.[p] || null;
  if (typeof p === 'string') {
    const hit = (EIGHTIES_CATALOG || []).find(c => c.title === p);
    return hit || { title: p, year: '' };
  }
  return { title: p.title || '', year: p.year || '' };
}

/** movieNight.phaseFor reads the clock (Tuesday 3pm opens picks, the last
 * hour is 'countdown', started_at/ended_at win). The `status` column is the
 * row's own word on it — the tick flips it to 'live' at 7pm and 'after' when
 * credits roll — so it overrides the clock where the two disagree. */
function safePhase(night, now) {
  if (!night) return null;
  if (night.status === 'cancelled') return null;
  if (night.status === 'after') return 'after';
  if (night.status === 'live') return 'live';
  try {
    const p = phaseFor(now, night);
    if (PHASES.includes(p)) return p;
  } catch {}
  // Defensive fallback so a malformed row still renders SOMETHING sensible.
  return night.locked_idx == null ? 'picks' : 'locked';
}

/** The host: whoever locked the pick, else Haley by default (she is the
 * default projectionist per the spec), else the first member we know of. */
function hostIdOf(night, list, defaultHostId) {
  if (night?.host_user_id) return night.host_user_id;
  if (night?.locked_by) return night.locked_by;
  // The integrator passes the projectionist's id (Haley). A display-name
  // regex here would flip the guest copy the day she renamed herself.
  if (defaultHostId) return defaultHostId;
  return list[0]?.id || null;
}

function fmtDate(iso, opts) {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: TZ, ...opts }).format(new Date(iso));
  } catch { return ''; }
}

/** "Thursday 7" — weekday + hour, ET, minutes only when they aren't :00. */
function fmtWhen(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: TZ, weekday: 'long', hour: 'numeric', minute: '2-digit', hour12: true,
    }).formatToParts(d);
    const get = (t) => (parts.find(p => p.type === t) || {}).value || '';
    const day = get('weekday'), h = get('hour'), m = get('minute');
    return m === '00' ? `${day} ${h}` : `${day} ${h}:${m}`;
  } catch { return ''; }
}

function countdownParts(targetIso, nowMs) {
  const diff = Math.max(0, new Date(targetIso).getTime() - nowMs);
  const mins = Math.floor(diff / 60000);
  return { days: Math.floor(mins / 1440), hours: Math.floor((mins % 1440) / 60), mins: mins % 60, totalMs: diff };
}

function fmtElapsed(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const mm = String(m).padStart(2, '0'), ss = String(sec).padStart(2, '0');
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

function sourceLabel(kind) {
  if (kind === 'youtube') return 'YouTube';
  if (kind === 'archive') return 'Internet Archive';
  return 'Your own screen';
}

/** One-line guidance under the link box, in the room's voice. */
function sourceHint(kind) {
  if (kind === 'external') return 'Plays on your own screen · we sync the clock';
  return `Plays inside the app · ${sourceLabel(kind)}`;
}

// ─── Styles (inline objects; the shared tokens keep it on-theme) ─────────────

const F_BODY = "'Cormorant Garamond', Georgia, serif";
const F_LABEL = "'Cinzel', serif";

const S = {
  card: {
    margin: '8px 10px 4px',
    padding: '14px 14px 12px',
    background: 'var(--ds-surface-1, #150b0d)',
    border: '1px solid var(--ds-line, rgba(196,168,130,0.18))',
    borderRadius: 'var(--ds-r-sm, 10px)',
    color: 'var(--ds-ink-hi, #f3e9dc)',
    fontFamily: F_BODY,
    boxSizing: 'border-box',
    maxWidth: '100%',
  },
  label: {
    fontFamily: F_LABEL, fontSize: '10px', fontWeight: 700, letterSpacing: '2.2px',
    textTransform: 'uppercase', color: 'var(--ds-gold, #c4a882)',
  },
  faint: { color: 'var(--ds-ink-faint, #7d6d66)', fontSize: '13px' },
  low: { color: 'var(--ds-ink-low, #9c8983)', fontSize: '14px' },
  title: { fontSize: '24px', lineHeight: 1.15, fontWeight: 600, margin: '6px 0 2px' },
  btn: {
    minHeight: '48px', width: '100%', padding: '10px 14px', marginTop: '10px',
    fontFamily: F_LABEL, fontSize: '12px', letterSpacing: '1.6px', textTransform: 'uppercase',
    color: 'var(--ds-surface-0, #0d0709)', background: 'var(--ds-gold, #c4a882)',
    border: 'none', borderRadius: 'var(--ds-r-pill, 999px)', cursor: 'pointer',
  },
  btnGhost: {
    minHeight: '44px', padding: '8px 14px', marginTop: '8px',
    fontFamily: F_LABEL, fontSize: '11px', letterSpacing: '1.4px', textTransform: 'uppercase',
    color: 'var(--ds-gold, #c4a882)', background: 'transparent',
    border: '1px solid var(--ds-gold, #c4a882)', borderRadius: 'var(--ds-r-pill, 999px)', cursor: 'pointer',
  },
  tile: (mine, theirs, lockable) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
    width: '100%', minHeight: '52px', padding: '10px 12px', marginTop: '8px',
    textAlign: 'left', cursor: 'pointer', fontFamily: F_BODY, fontSize: '17px',
    color: 'var(--ds-ink-hi, #f3e9dc)',
    background: mine ? 'var(--ds-gold-a15, rgba(196,168,130,0.12))' : 'var(--ds-surface-2, #1d1114)',
    border: `1px solid ${mine || lockable ? 'var(--ds-gold, #c4a882)' : theirs ? 'var(--ds-accent, #c62828)' : 'var(--ds-line, rgba(196,168,130,0.18))'}`,
    borderRadius: 'var(--ds-r-sm, 10px)',
  }),
  input: {
    width: '100%', minHeight: '44px', padding: '10px 12px', boxSizing: 'border-box',
    fontFamily: F_BODY, fontSize: '16px', // 16px: iOS Safari zooms on anything smaller
    color: 'var(--ds-ink-hi, #f3e9dc)', background: 'var(--ds-surface-0, #0d0709)',
    border: '1px solid var(--ds-line, rgba(196,168,130,0.18))', borderRadius: 'var(--ds-r-sm, 10px)',
  },
  countNum: { fontSize: '28px', fontWeight: 600, fontVariantNumeric: 'tabular-nums', lineHeight: 1 },
};

const KEYFRAMES = `
@keyframes mfml-mn-pulse {
  0%, 100% { box-shadow: 0 0 0 0 var(--ds-gold-a22, rgba(196,168,130,0.22)); }
  50%      { box-shadow: 0 0 0 10px transparent; }
}
.mfml-mn-pulse { animation: mfml-mn-pulse 1.8s ease-in-out infinite; }
/* Perforated tear-off edge: a dashed line with the paper punched out above
   and below it — the two half-circles sit ON the perforation, the way a real
   stub is notched where you tear, so it reads as a ticket and not a card. */
.mfml-mn-stub { position: relative; }
.mfml-mn-perf { position: relative; border-left: 2px dashed var(--ds-gold-a22, rgba(196,168,130,0.35)); }
.mfml-mn-perf::before, .mfml-mn-perf::after {
  content: ''; position: absolute; left: -10px; width: 18px; height: 18px;
  border-radius: 50%;
  background: var(--ds-surface-1, #150b0d);
  border: 1px solid var(--ds-gold-a22, rgba(196,168,130,0.35));
}
.mfml-mn-perf::before { top: -10px; }
.mfml-mn-perf::after { bottom: -10px; }
@media (prefers-reduced-motion: reduce) { .mfml-mn-pulse { animation: none; } }
`;

// ─── The card ────────────────────────────────────────────────────────────────

export default function MovieNightCard({
  channelId,
  userId,
  members,
  isMobile = true,
  onLiveMinute,
  onEnterLivingRoom,
  review,
  defaultHostId = null,
  onAfter,
}) {
  const [night, setNight] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [linkDraft, setLinkDraft] = useState('');
  const [kept, setKept] = useState(false);
  // One warm line for whatever didn't go through (load, vote, link, keep).
  // Cleared by the next thing that works, so it never lingers as a scold.
  const [note, setNote] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const keptOnceRef = useRef(false);
  const lastMinuteRef = useRef(-1);
  const nightRef = useRef(null);
  nightRef.current = night;

  const list = useMemo(() => memberList(members), [members]);
  const other = useMemo(() => list.find(m => m.id !== userId) || null, [list, userId]);
  const phase = safePhase(night, new Date(nowMs));
  const hostId = hostIdOf(night, list, defaultHostId);
  const isHost = !!night && !!userId && hostId === userId;

  // ── Load the next night for this channel ─────────────────────────────────
  // Primary: the next row that isn't finished. Fallback: a night that ended
  // in the last two days, so the stub is still there to keep the morning
  // after (the integrator's load rule is "status != after"; without this the
  // stub would only ever exist in the tab that was open when the film ended).
  const load = useCallback(async () => {
    if (!channelId) { setLoaded(true); return; }
    try {
      const floor = new Date(Date.now() - STALE_NIGHT_MS).toISOString();
      const { data, error } = await supabase
        .from('movie_nights')
        .select('*')
        .eq('channel_id', channelId)
        .neq('status', 'after')
        .neq('status', 'cancelled')
        .gte('night_at', floor)
        .order('night_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      if (data) { setNight(data); setLoadFailed(false); setLoaded(true); return; }
      const since = new Date(Date.now() - 48 * 3600 * 1000).toISOString();
      const { data: recent, error: recentErr } = await supabase
        .from('movie_nights')
        .select('*')
        .eq('channel_id', channelId)
        .eq('status', 'after')
        .gte('night_at', since)
        .order('night_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (recentErr) throw recentErr;
      setNight(recent || null);
      setLoadFailed(false);
    } catch {
      // Don't pretend there's no night — say the room didn't answer.
      setLoadFailed(true);
    } finally { setLoaded(true); }
  }, [channelId]);

  useEffect(() => { load(); }, [load]);

  // ── Realtime: both players see votes / lock / link / phase live ──────────
  // One subscription per channel (not per row) so the Tuesday INSERT flips
  // the empty state without a reload, and UPDATEs to our row merge in.
  useEffect(() => {
    if (!channelId) return;
    let ch;
    try {
      ch = supabase
        .channel(`movie-night-${channelId}`)
        .on('postgres_changes', {
          event: '*', schema: 'public', table: 'movie_nights', filter: `channel_id=eq.${channelId}`,
        }, (payload) => {
          const row = payload?.new;
          if (!row?.id) return;
          const cur = nightRef.current;
          if (cur && row.id === cur.id) { setNight(prev => ({ ...prev, ...row })); return; }
          // A new night arrived (Cruz's Tuesday post) — adopt it when we have
          // nothing, or when what we hold is finished: by its status, or by
          // the clock. A night nobody started reads 'after' six hours on while
          // its status column still says 'locked'; judging by status alone
          // left that stale row sitting on top of the new one all week.
          const curPhase = safePhase(cur, new Date());
          if (!cur || curPhase == null || curPhase === 'after') {
            if (row.status !== 'after' && row.status !== 'cancelled') setNight(row);
          }
        })
        .subscribe();
    } catch {}
    return () => { try { if (ch) supabase.removeChannel(ch); } catch {} };
  }, [channelId]);

  // ── Clock: minute ticks normally, second ticks when the elapsed counter or
  // the pulsing countdown is on screen. ────────────────────────────────────
  useEffect(() => {
    if (!night) return;
    const fast = phase === 'live' || phase === 'countdown';
    const id = setInterval(() => setNowMs(Date.now()), fast ? 1000 : 30000);
    return () => clearInterval(id);
  }, [night, phase]);

  // ── Live minute events for the integrator (Cruz's commentary hooks in
  // here; the card itself never generates a line of it). ─────────────────
  const startedAtMs = night ? new Date(night.started_at || night.night_at).getTime() : 0;
  const elapsedMs = phase === 'live' ? Math.max(0, nowMs - startedAtMs) : 0;
  const liveMinute = Math.floor(elapsedMs / 60000);
  useEffect(() => {
    if (phase !== 'live') { lastMinuteRef.current = -1; return; }
    if (liveMinute === lastMinuteRef.current) return;
    lastMinuteRef.current = liveMinute;
    try { onLiveMinute?.(night, liveMinute); } catch {}
  }, [phase, liveMinute, night, onLiveMinute]);

  // Seed the link box from a saved link so the host can correct it.
  useEffect(() => { setLinkDraft(night?.video?.url || ''); }, [night?.id, night?.video?.url]);
  const afterFiredRef = useRef(null);
  useEffect(() => {
    if (phase !== 'after' || !night?.id || afterFiredRef.current === night.id) return;
    afterFiredRef.current = night.id;
    try { onAfter?.(night); } catch {}
  }, [phase, night, onAfter]);

  // "Keep the stub" is one tap forever: remember locally that it was kept so
  // a remount doesn't offer it twice (the RPC is expected to be idempotent
  // too, but the button copy shouldn't flip back).
  useEffect(() => {
    if (!night?.id) return;
    try { setKept(localStorage.getItem(`mfml_movie_stub_kept_${night.id}`) === '1'); } catch { setKept(false); }
  }, [night?.id]);

  // ── Persist helpers ──────────────────────────────────────────────────────
  // `guard` makes the write compare-and-set: { votesWere, unlocked }. The vote
  // needs it because `votes` is replaced wholesale — two first taps in the
  // same second would each write their own map and one vote would vanish,
  // and a lock sent from a screen that hadn't seen the other lock yet would
  // overwrite it. PostgREST compares jsonb semantically, so the stringified
  // snapshot's key order doesn't matter. Zero rows back means the board
  // moved under the tap: reload and say so, never pretend it landed.
  const patchNight = useCallback(async (patch, guard) => {
    const cur = nightRef.current;
    if (!cur?.id || !patch) return;
    setNight(prev => ({ ...prev, ...patch }));   // optimistic; realtime confirms
    setBusy(true);
    setNote('');
    try {
      let q = supabase.from('movie_nights').update(patch).eq('id', cur.id);
      if (guard?.votesWere) q = q.eq('votes', JSON.stringify(guard.votesWere));
      if (guard?.unlocked) q = q.is('locked_idx', null);
      if (guard?.live) q = q.eq('status', 'live');
      const { data, error } = await q.select('id');
      if (error) throw error;
      if (guard && !(Array.isArray(data) && data.length)) {
        setNote('The board moved under your tap — here it is as it stands.');
        load();
      }
    } catch {
      setNote("That didn't stick — the Pack House didn't get it. Try once more.");
      load();                                       // roll back to the truth
    } finally { setBusy(false); }
  }, [load]);

  const vote = useCallback((idx) => {
    if (busy || !night || !userId) return;
    // resolveVote ignores taps from anyone outside `members`, so the list
    // must include me even if the profiles cache hasn't loaded yet — and the
    // other player's id from an existing vote, so her vote still counts as
    // "the other key" before her profile arrives.
    const memberIds = new Set(list.map(m => m.id));
    memberIds.add(userId);
    Object.keys(night.votes || {}).forEach(id => memberIds.add(id));
    let r = null;
    try { r = resolveVote(night.votes || {}, userId, idx, { members: [...memberIds], locked_idx: night.locked_idx ?? null }); } catch {}
    if (!r || !r.votes) return;
    if (r.votes[userId] !== idx) return;                 // rejected (already locked, bad idx)
    const patch = { votes: r.votes };
    if (r.locked_idx != null) {
      // Second key turned: lock it, and whoever locks hosts (the tick seeded
      // Haley as the default host; the lock is the override the spec names).
      patch.locked_idx = r.locked_idx;
      patch.locked_by = r.lockedBy || userId;
      patch.host_user_id = r.lockedBy || userId;
      patch.status = 'locked';
    }
    patchNight(patch, { votesWere: night.votes || {}, unlocked: true });
  }, [busy, night, userId, list, patchNight]);

  const parsedLink = useMemo(() => {
    const url = linkDraft.trim();
    if (!url) return null;
    try { return parseVideoUrl(url); } catch { return null; }
  }, [linkDraft]);

  const saveLink = useCallback(() => {
    const url = linkDraft.trim();
    if (!url || !parsedLink) return;
    // parseVideoUrl returns {kind, id|file} for YouTube/Archive and {kind, url}
    // for everything else; the row wants the pasted url on all of them.
    patchNight({ video: { ...parsedLink, url: parsedLink.url || url } });
  }, [linkDraft, parsedLink, patchNight]);

  const keepStub = useCallback(async () => {
    if (!night?.id || keptOnceRef.current || kept) return;
    keptOnceRef.current = true;
    setBusy(true);
    setNote('');
    try {
      const { error } = await supabase.rpc('movie_night_stub', { p_night_id: night.id });
      if (error) throw error;
      setKept(true);
      try { localStorage.setItem(`mfml_movie_stub_kept_${night.id}`, '1'); } catch {}
    } catch {
      keptOnceRef.current = false;
      setNote("The stub didn't make it to the Shelf. Tap again and it will.");
    } finally { setBusy(false); }
  }, [night?.id, kept]);

  // ── Render ───────────────────────────────────────────────────────────────
  const pad = isMobile ? '12px 12px 10px' : '16px 18px 14px';

  if (!loaded) return null;   // no skeleton: the channel shouldn't jump for a card that may not exist

  if (!night && loadFailed) {
    return (
      <div style={{ ...S.card, padding: pad }} data-phase="error">
        <div style={S.label}>🎬 Cruz's Movie Night</div>
        <div style={{ ...S.low, marginTop: '6px' }}>The Pack House isn't answering right now.</div>
        <button type="button" onClick={load} style={{ ...S.btnGhost, width: '100%' }}>
          Knock again
        </button>
      </div>
    );
  }

  if (!night || !phase) {
    return (
      <div style={{ ...S.card, padding: pad }} data-phase="empty">
        <div style={S.label}>🎬 Cruz's Movie Night</div>
        <div style={{ ...S.low, marginTop: '6px' }}>Cruz posts picks Tuesday at 3.</div>
      </div>
    );
  }

  const picks = (Array.isArray(night.picks) ? night.picks : []).map(pickOf).filter(Boolean);
  const votes = (night.votes && typeof night.votes === 'object') ? night.votes : {};
  const myVote = userId != null ? votes[userId] : undefined;
  const otherVote = other ? votes[other.id] : undefined;
  const otherHasVoted = otherVote !== undefined && otherVote !== null;
  const otherName = other?.name || 'the other one';
  const winner = night.locked_idx != null ? picks[night.locked_idx] : null;
  const hostName = nameOf(list, hostId, 'Haley');

  return (
    <div style={{ ...S.card, padding: pad }} data-phase={phase}>
      <style>{KEYFRAMES}</style>

      {/* ── UPCOMING: the row exists but Tuesday 3pm hasn't come ── */}
      {phase === 'upcoming' && (
        <>
          <div style={S.label}>🎬 Cruz's Movie Night · {fmtWhen(night.night_at)}</div>
          <div style={{ ...S.low, marginTop: '6px' }}>Cruz posts picks Tuesday at 3.</div>
        </>
      )}

      {/* ── PICKS ── */}
      {phase === 'picks' && (
        <>
          <div style={S.label}>🎬 Cruz's Movie Night · {fmtWhen(night.night_at)}</div>
          <div style={{ ...S.low, marginTop: '4px' }}>
            Three picks. First tap votes, second tap locks it.
          </div>
          {picks.map((p, i) => {
            const mine = myVote === i;
            const theirs = otherHasVoted && otherVote === i;
            const lockable = otherHasVoted && !mine;
            return (
              <button
                key={`${p.title}-${i}`}
                type="button"
                onClick={() => vote(i)}
                disabled={busy || myVote === i}
                aria-pressed={mine}
                aria-label={`${p.title}${p.year ? ` (${p.year})` : ''}${theirs ? `, ${otherName}'s vote` : ''}${otherHasVoted ? ', tap to lock' : ', tap to vote'}`}
                style={S.tile(mine, theirs, lockable)}
              >
                <span>
                  {p.title}
                  {p.year ? <span style={{ ...S.faint, marginLeft: '8px' }}>· {p.year}</span> : null}
                </span>
                <span style={{ ...S.label, fontSize: '9px', whiteSpace: 'nowrap' }}>
                  {mine ? 'Your vote' : theirs ? `${otherName}'s` : otherHasVoted ? 'Lock' : ''}
                </span>
              </button>
            );
          })}
          <div style={{ ...S.faint, marginTop: '8px' }}>
            {myVote !== undefined && myVote !== null && !otherHasVoted
              ? `Waiting on ${otherName}.`
              : otherHasVoted
                ? `${otherName} voted. Your tap locks the night — agree, or overrule.`
                : 'Nobody has voted yet.'}
          </div>
        </>
      )}

      {/* ── LOCKED ── */}
      {phase === 'locked' && (
        <>
          <div style={S.label}>🎬 Locked · {fmtWhen(night.night_at)}</div>
          <div style={S.title}>{winner?.title || 'A movie'}</div>
          {winner?.year ? <div style={S.faint}>{winner.year}</div> : null}
          <Countdown target={night.night_at} nowMs={nowMs} />
          {isHost ? (
            <div style={{ marginTop: '12px' }}>
              <div style={{ ...S.label, marginBottom: '6px' }}>Paste the link</div>
              <input
                type="url"
                inputMode="url"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder="A link you can watch it on"
                value={linkDraft}
                onChange={e => setLinkDraft(e.target.value)}
                aria-label="Movie link"
                style={S.input}
              />
              <div style={{ ...S.faint, marginTop: '6px' }} aria-live="polite">
                {linkDraft.trim()
                  ? (parsedLink
                    ? sourceHint(parsedLink.kind)
                    : 'That doesn\'t look like a link yet.')
                  : (night.video?.url ? `Saved · ${sourceLabel(night.video.kind)}` : 'YouTube and Internet Archive play inside the app. Anything else, we sync the clock.')}
              </div>
              <button
                type="button"
                onClick={saveLink}
                disabled={busy || !parsedLink}
                style={{ ...S.btn, opacity: parsedLink ? 1 : 0.5 }}
              >
                {night.video?.url ? 'Update the link' : 'Save the link'}
              </button>
            </div>
          ) : (
            <div style={{ ...S.low, marginTop: '12px' }}>
              {night.video?.url
                ? `${hostName} has the movie ready · ${sourceLabel(night.video.kind)}`
                : `${hostName} is bringing the movie.`}
            </div>
          )}
        </>
      )}

      {/* ── COUNTDOWN (final hour) ── */}
      {phase === 'countdown' && (
        <>
          <div style={S.label}>🎬 Tonight · {fmtWhen(night.night_at)}</div>
          <div style={S.title}>{winner?.title || 'A movie'}</div>
          <div className="mfml-mn-pulse" style={{ borderRadius: 'var(--ds-r-sm, 10px)', padding: '4px 0' }}>
            <Countdown target={night.night_at} nowMs={nowMs} />
          </div>
          {!night.video?.url && (
            <div style={{ ...S.faint, marginTop: '6px' }}>
              {isHost ? 'No link yet — you can still paste one in the living room.' : `${hostName} is bringing the movie.`}
            </div>
          )}
          <button type="button" onClick={() => onEnterLivingRoom?.(night)} style={S.btn}>
            Enter the living room
          </button>
        </>
      )}

      {/* ── LIVE ── */}
      {phase === 'live' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 160px', minWidth: 0 }}>
            <div style={S.label}>🎬 Now playing</div>
            {/* Title may ellipsize; the clock and Cruz's presence never do —
                the running time is the one live thing on the strip. */}
            <div style={{ fontSize: '17px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {winner?.title || 'The movie'}
            </div>
            <div style={{ ...S.faint, whiteSpace: 'nowrap' }}>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtElapsed(elapsedMs)}</span>
              <span style={{ marginLeft: '8px' }}>· Cruz is here</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onEnterLivingRoom?.(night)}
            style={{ ...S.btnGhost, marginTop: 0, flex: '0 0 auto' }}
          >
            Back to the couch
          </button>
          {userId && hostId === userId && (
            <button
              type="button"
              disabled={busy}
              onClick={() => patchNight({ status: 'after', ended_at: new Date().toISOString() }, { live: true })}
              style={{ ...S.btn, marginTop: '8px', background: 'transparent', color: 'var(--ds-ink-low)', border: '1px solid var(--ds-line)' }}
              title="Ends the night for both of you and mints the ticket stubs"
            >
              🎞 Credits rolled
            </button>
          )}
        </div>
      )}

      {/* ── AFTER: the ticket stub ── */}
      {phase === 'after' && (
        <TicketStub
          night={night}
          list={list}
          winner={winner}
          review={review}
          kept={kept}
          busy={busy}
          onKeep={keepStub}
        />
      )}

      {note ? (
        <div role="status" style={{ ...S.faint, marginTop: '8px', color: 'var(--ds-accent, #c62828)' }}>
          {note}
        </div>
      ) : null}
    </div>
  );
}

// ─── Countdown (days / hours / minutes, tabular) ─────────────────────────────

function Unit({ n, label }) {
  return (
    <div style={{ textAlign: 'center', minWidth: '52px' }}>
      <div style={S.countNum}>{String(n).padStart(2, '0')}</div>
      <div style={{ ...S.label, fontSize: '9px', marginTop: '4px' }}>{label}</div>
    </div>
  );
}

function Countdown({ target, nowMs }) {
  const c = countdownParts(target, nowMs);
  return (
    <div
      role="timer"
      aria-live="off"
      aria-label={`${c.days} days ${c.hours} hours ${c.mins} minutes to showtime`}
      style={{ display: 'flex', gap: '10px', justifyContent: 'flex-start', marginTop: '10px' }}
    >
      {c.days > 0 && <Unit n={c.days} label="days" />}
      <Unit n={c.hours} label="hours" />
      <Unit n={c.mins} label="min" />
    </div>
  );
}

// ─── Ticket stub ─────────────────────────────────────────────────────────────

function TicketStub({ night, list, winner, review, kept, busy, onKeep }) {
  const title = winner?.title || 'Movie Night';
  const year = winner?.year || '';
  const dateLine = fmtDate(night.night_at, { month: 'short', day: '2-digit', year: 'numeric' });
  const timeLine = fmtDate(night.night_at, { hour: 'numeric', minute: '2-digit', hour12: true });
  const seats = list.map(m => initialsOf(m.name));
  // The same text the keepsake carries — so the stub on screen and the one on
  // the Shelf read identically.
  let finePrint = '';
  try { finePrint = ticketStub(night, { pickTitle: title, year, attendees: list.map(m => m.name) })?.description || ''; } catch {}
  const reviewText = (review || night.review || '').trim();

  return (
    <div className="mfml-mn-stub" style={{
      display: 'flex', alignItems: 'stretch',
      background: 'var(--ds-surface-2, #1d1114)',
      border: '1px solid var(--ds-gold-a22, rgba(196,168,130,0.35))',
      borderRadius: 'var(--ds-r-sm, 10px)',
      overflow: 'visible',
    }}>
      {/* Main body of the ticket */}
      <div style={{ flex: '1 1 auto', minWidth: 0, padding: '12px 12px 12px 16px' }}>
        <div style={S.label}>🎟️ Admit two · Pack House</div>
        <div style={{ ...S.title, fontSize: '22px' }}>{title}</div>
        {year ? <div style={S.faint}>{year}</div> : null}
        {/* flexWrap: on the 375px mini the body is ~230px wide; the three
            columns fit at 14px but wrap cleanly rather than overflow if a
            long month or a third seat ever shows up. */}
        <div style={{ display: 'flex', gap: '14px', rowGap: '6px', flexWrap: 'wrap', marginTop: '8px', fontVariantNumeric: 'tabular-nums' }}>
          <div>
            <div style={{ ...S.label, fontSize: '9px' }}>Date</div>
            <div style={{ fontSize: '14px' }}>{dateLine}</div>
          </div>
          <div>
            <div style={{ ...S.label, fontSize: '9px' }}>Show</div>
            <div style={{ fontSize: '14px' }}>{timeLine}</div>
          </div>
          <div>
            <div style={{ ...S.label, fontSize: '9px' }}>Seats</div>
            <div style={{ fontSize: '14px' }}>{seats.join(' · ')}</div>
          </div>
        </div>
        {finePrint ? <div style={{ ...S.faint, marginTop: '8px', fontSize: '12px' }}>{finePrint}</div> : null}
        <div style={{ marginTop: '10px' }}>
          <div style={{ ...S.label, fontSize: '9px' }}>Cruz's review</div>
          <div style={{ fontSize: '15px', lineHeight: 1.45, fontStyle: reviewText ? 'italic' : 'normal', color: reviewText ? 'var(--ds-ink-hi, #f3e9dc)' : 'var(--ds-ink-faint, #7d6d66)', whiteSpace: 'pre-wrap' }}>
            {reviewText || 'Cruz is still writing his review.'}
          </div>
        </div>
      </div>
      {/* Tear-off tab: the keep button lives on the perforated side */}
      {/* The tab is 60px wide, not 96: the button's label runs vertically, so
          its 44px tap width is the cross-axis and the extra was dead paper
          stolen from the title on a 375px screen. */}
      <div className="mfml-mn-perf" style={{
        flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '10px 8px', minWidth: '60px',
      }}>
        <button
          type="button"
          onClick={onKeep}
          disabled={kept || busy}
          aria-label={kept ? 'On the Shelf' : 'Keep the stub'}
          style={{
            ...S.btnGhost, marginTop: 0, minHeight: '44px', minWidth: '44px',
            padding: '12px 8px', fontSize: '10px',
            writingMode: 'vertical-rl', transform: 'rotate(180deg)',
            opacity: kept ? 0.7 : 1,
          }}
        >
          {kept ? 'On the Shelf' : 'Keep the stub'}
        </button>
      </div>
    </div>
  );
}

// ─── Home-screen one-liner ───────────────────────────────────────────────────
// 'Movie night Thursday 7 · The Breakfast Club · locked' — the integrator drops
// this on the RP home; it reads the row it's handed and subscribes to nothing.

export function MovieNightHomeCard({ night, onClick }) {
  const phase = safePhase(night, new Date());
  const inner = (() => {
    if (!night) return 'Cruz posts picks Tuesday at 3.';
    const picks = (Array.isArray(night.picks) ? night.picks : []).map(pickOf).filter(Boolean);
    const winner = night.locked_idx != null ? picks[night.locked_idx] : null;
    if (!phase) return 'Cruz posts picks Tuesday at 3.';
    const state = phase === 'upcoming' ? 'picks Tuesday at 3'
      : phase === 'picks' ? 'vote open'
      : phase === 'locked' ? 'locked'
        : phase === 'countdown' ? 'starting soon'
          : phase === 'live' ? 'now playing'
            : 'stub ready';
    const bits = [`Movie night ${fmtWhen(night.night_at)}`];
    if (winner?.title) bits.push(winner.title);
    bits.push(state);
    return bits.join(' · ');
  })();
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      style={{
        ...S.card, margin: 0, padding: '10px 12px', width: '100%', textAlign: 'left',
        minHeight: '44px', display: 'flex', alignItems: 'center', gap: '8px',
        cursor: onClick ? 'pointer' : 'default', fontSize: '15px',
      }}
    >
      <span aria-hidden="true">🎬</span>
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inner}</span>
    </Tag>
  );
}
